import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import axios from 'axios';
import authStore from '@stores/AuthStore';
import uiStore from '@stores/UiStore';
import type { HttpErrHandler, SmpAxiosError } from '@utils/handleError';
import { handleError } from '@utils/handleError';

import { getDomainFromBaseUrl } from '@utils/urlUtils';
import i18n from '../locales/i18n';
import { getErrorMessage } from '../locales/i18nErrorMessage';
import localStorage from './browserStorage/localStorage/LocalStorage';
import sessionStorage from './browserStorage/sessionStorage/SessionStorage';

const axiosInstance = axios.create();

interface SystemErrorHandler extends HttpErrHandler {
  400: (error: AxiosError<{ errorCode: { messageId: string } }>) => void;
  401: (error: AxiosError<{ errorCode: { messageId: string } }>) => void;
  403: (error: AxiosError<{ errorCode: { messageId: string } }>) => void;
}

const systemHandler: SystemErrorHandler = {
  400(error) {
    if (error.config?.method === 'get') {
      // uiStore.errorNotiUiStore.open('서비스 실패');
    } else {
      // FIXME: 에러 처리 정책 결정한 후 수정 필요
      // const { message } = error.response?.data as { message: string };
      // uiStore.errorNotiUiStore.open(message);
    }
  },
  401(error) {
    const serviceError = error.response?.data;

    // AccessToken과 refreshToken이 만료되었을 때, 세션 만료 모달 표시
    if (
      serviceError &&
      (error.response?.data.errorCode?.messageId === 'AUTH.ERROR.NO_TOKEN_ERROR' ||
        error.response?.data.errorCode?.messageId === 'AUTH.ERROR.TOKEN_INVALID' ||
        error.response?.data.errorCode?.messageId === 'AUTH.ERROR.CONCURRENT_LOGIN_DETECTED')
    ) {
      const errorInfo = getErrorMessage(error);
      // errorType (키)가 있으면 키를 전달, 없으면 message 전달
      const errorKey = errorInfo.errorType ? `Error.${errorInfo.errorType}` : errorInfo.message;
      uiStore.openSessionExpiredModal(errorKey);
      axiosInstance.defaults.headers.common.Authorization = undefined;
      // NOTE: authStore.logout()은 모달에서 확인 버튼을 누를 때 호출됨 (Employee.tsx의 handleCloseSessionOut)
    }
  },
  403(error) {
    // 제한 토큰으로 비허용 API 접근 시 테넌트 선택 필요
    if (error.response?.data?.errorCode?.messageId === 'AUTH.ERROR.TENANT_SELECTION_REQUIRED') {
      uiStore.openSessionExpiredModal('Error.TENANT_SELECTION_REQUIRED');
    }
  },
  common(err) {
    const serviceError = err.response?.data;
    if (serviceError) {
      console.error({
        'layer :': 'axios common err handler',
        'status: ': err.response?.status,
        'serviceErrCode: ': serviceError.errorCode?.messageId,
        'serviceErrMessage: ': serviceError.message,
      });
    }
  },
  default(err) {
    console.error('axios err default handler: ', err);
  },
};

let timer: NodeJS.Timeout | null = null;

const logout = () => {
  authStore.logout();
  // timeout으로 인한 로그아웃은 에러 메시지 없이 모달 표시
  uiStore.openSessionExpiredModal();
  if (timer) {
    clearTimeout(timer);
  }
};

interface AxiosType {
  instance: AxiosInstance;
  get: <T>(url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
  post: <T>(url: string, data?: UnknownObject, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
  delete: <T>(url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
  put: <T>(url: string, data?: UnknownObject, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
  patch: <T>(url: string, data?: UnknownObject, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
  getDefaultHeader: () => RawAxiosRequestHeaders;
}

if (import.meta.env.VITE_BASE_URL) {
  axiosInstance.defaults.baseURL = import.meta.env.VITE_BASE_URL;
} else {
  axiosInstance.defaults.baseURL = window.location.origin;
}

axiosInstance.defaults.withCredentials = true;

axiosInstance.defaults.headers.common['Content-Type'] = 'application/json';

const userLang = localStorage.get<string>('lang') as string;
const isSecure = window.location.protocol === 'https:';
const secureAttr = isSecure ? ' secure;' : '';

// COMPANY_ID에 따른 도메인 설정
const domainAttr = getDomainFromBaseUrl();

if (userLang) {
  i18n.changeLanguage(userLang);
  localStorage.set('lang', userLang);
  const cookieString = `SMP_lang=${userLang}; path=/;${secureAttr} ${domainAttr}`;
  document.cookie = cookieString;
} else {
  const browserLang = navigator.language;
  const formattingLang = browserLang.split('-');
  const detectedLang = formattingLang[0].toLowerCase();
  i18n.changeLanguage(detectedLang);
  localStorage.set('lang', detectedLang);
  const cookieString = `SMP_lang=${detectedLang}; path=/;${secureAttr} ${domainAttr}`;
  document.cookie = cookieString;
}

const tk = sessionStorage.get('tk');
if (tk && typeof tk === 'string') {
  axiosInstance.defaults.headers.common.Authorization = tk;
}

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    if (timer) clearTimeout(timer);
    const hasToken = sessionStorage.get('tk');
    if (hasToken) {
      timer = setTimeout(() => {
        logout();
      }, 21600000);
    }
    return response;
  },
  (error: SmpAxiosError) => {
    if (timer) clearTimeout(timer);
    const hasToken = sessionStorage.get('tk');
    if (hasToken) {
      timer = setTimeout(() => {
        logout();
      }, 21600000);
    }
    handleError(error, systemHandler);
    return Promise.reject(error);
  },
);

const API: AxiosType = {
  instance: axiosInstance,

  getDefaultHeader() {
    return authStore.curTenant.id ? { 'X-TenantID': authStore.curTenant.id } : {};
  },

  async get<T>(url: string, config: AxiosRequestConfig = {}) {
    const response = await this.instance.get<T>(url, {
      ...config,
      headers: { ...this.getDefaultHeader(), ...config.headers },
    });
    const tk = response.headers.authorization;

    if (tk) {
      const newToken = response.headers.authorization as string;
      this.instance.defaults.headers.common.Authorization = newToken;
      sessionStorage.set('tk', newToken);
    }
    return response;
  },

  async post<T>(url: string, data = {}, config: AxiosRequestConfig = {}) {
    const response = await this.instance.post<T, AxiosResponse<T>>(url, data, {
      ...config,
      headers: { ...this.getDefaultHeader(), ...config.headers },
    });
    const tk = response.headers.authorization;

    if (tk) {
      // 토큰을 동기적으로 한 번에 업데이트
      const newToken = response.headers.authorization as string;
      this.instance.defaults.headers.common.Authorization = newToken;
      sessionStorage.set('tk', newToken);
    }
    return response;
  },

  async put<T>(url: string, data = {}, config: AxiosRequestConfig = {}) {
    const response = await this.instance.put<T>(url, data, {
      ...config,
      headers: { ...this.getDefaultHeader(), ...config.headers },
    });
    const tk = response.headers.authorization;
    if (tk) {
      const newToken = response.headers.authorization as string;
      this.instance.defaults.headers.common.Authorization = newToken;
      sessionStorage.set('tk', newToken);
    }
    return response;
  },

  async delete<T>(url: string, config: AxiosRequestConfig = {}) {
    const response = await this.instance.delete<T>(url, {
      ...config,
      headers: { ...this.getDefaultHeader(), ...config.headers },
    });
    const tk = response.headers.authorization;
    if (tk) {
      const newToken = response.headers.authorization as string;
      this.instance.defaults.headers.common.Authorization = newToken;
      sessionStorage.set('tk', newToken);
    }
    return response;
  },

  async patch<T>(url: string, data = {}, config: AxiosRequestConfig = {}) {
    const response = await this.instance.patch<T>(url, data, {
      ...config,
      headers: { ...this.getDefaultHeader(), ...config.headers },
    });
    const tk = response.headers.authorization;
    if (tk) {
      const newToken = response.headers.authorization as string;
      this.instance.defaults.headers.common.Authorization = newToken;
      sessionStorage.set('tk', newToken);
    }
    return response;
  },
};

export default API;
